import { Loop as LoopIcon } from '@carbon/icons-react';
import { Center, HorizBox, Pad, PadBox } from 'component/basics';
import { CTAButton, SubtleButton } from 'component/button';
import { colorBorderPrimary, colorButtonAccent, colorSurfaceSecondary, colorTextOnAccentColorBg, colorTextPrimary } from 'component/color';
import { ColoredBallPill } from 'component/colorpill';
import { type Comment, type CommentConfig, commentTable, postCommentAsync } from 'component/comment';
import { TextFieldButton } from 'component/form';
import { PollPicker, PollResult } from 'component/poll';
import { RatingSelector, type RatingSelectorProps } from 'component/ratingselector';
import { UtilityText } from 'component/text';
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import type { QuestionConfig } from 'structure/question';
import { MyAnswerPreview } from 'structure/question';
import { useAppData } from 'util/appdataContext';
import type { Datastore } from 'util/datastore';
import { useCollection, useDatastore, useObject, usePersonaKey } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { REPLACE, useConfig } from 'util/features';
import { useScreenParams } from 'util/params';
import type { Feature, TLabel } from 'util/stdtypes';
import { labelToKey, t } from 'util/stdtypes';

// Extended comment type for slider functionality
export type CommentWithSlider = Comment & {
    slider?: number;
};
export type CommentSliderFeatureConfig = {
    commentSliderLabels: TLabel[];
    commentSliderVisualization: React.ComponentType<any> | null;
    commentSliderSelector: React.ComponentType<any>;
    commentSliderDefault: number | null;
    commentSliderPrompt: TLabel;
};
export const CommentSliderFeature: Feature<QuestionConfig, CommentSliderFeatureConfig> = {
    name: 'Comment Slider',
    key: 'commentslider',
    providesConfig: {
        composerPreview: AnswerInputOnlyForCommentWithText,
        pageTopWidgets: [CommentSliderPageTopWidget],
        commentFilters: [getCommentsWithSelectedPosition],
        commentEditTopWidgets: [CommentSliderEditCommentTopWidget],
        commentAboveWidgets: [State],
        commentAllowEmpty: true,
        commentPostBlockers: [commentPostBlocker],
        commentPostCheckers: [addDefaultSliderPosition],
        composerSubtitle: () => t('Public response'),
    },
    receivesConfigDefaults: {
        commentSliderLabels: [
            t('Strongly no'),
            t('No with reservations'),
            t("It's complicated"),
            t('Yes with reservations'),
            t('Strongly yes'),
        ],
        commentSliderVisualization: null,
        commentSliderSelector: RatingSelector,
        commentSliderDefault: 3,
        commentSliderPrompt: t('Slide to respond:'),
    },
    subscreens: {
        recordingVote: () => {
            return <RecordingVoteScreen />;
        },
    },
};

function commentPostBlocker(datastore: Datastore, comment: Comment) {
    const { commentSliderDefault } = datastore.getConfig<CommentSliderFeatureConfig>();

    if (comment.replyTo) {
        return !comment.text;
    } else {
        return !(comment as CommentWithSlider).slider && !commentSliderDefault;
    }
}

async function addDefaultSliderPosition(datastore: Datastore, comment: Comment) {
    const { commentSliderDefault } = datastore.getConfig<CommentSliderFeatureConfig>();

    if (!(comment as CommentWithSlider).slider && commentSliderDefault) {
        return { allow: true, commentProps: { slider: commentSliderDefault } };
    } else {
        return { allow: true };
    }
}

export const CommentSliderDisableAnonymousFeature: Feature<CommentSliderFeatureConfig & CommentConfig, {}> = {
    name: 'CS Disable Anonymous',
    key: 'commentslider_disable_anonymous',
    parentFeature: 'commentslider',
    providesConfig: {
        commentAllowEmpty: false,
        composerSubtitle: () => t('Public response'),
    },
};

const colorStronglyYes = '#068287';
const colorYesWithReservations = '#00af88';
const colorItsComplicated = '#ffc841';
const colorNoWithReservations = '#fa7355';
const colorStronglyNo = '#f53c18';

const ratingColors = [colorStronglyNo, colorNoWithReservations, colorItsComplicated, colorYesWithReservations, colorStronglyYes];

function CommentSliderVisualizationRedirect({ comments }) {
    const ratingCounts = countRatings(comments);
    const commentsWithSlider = comments.filter((c) => c.slider);

    if (commentsWithSlider.length == 0) {
        return null;
    }
    return <HorizBarVisualization commentsWithSlider={commentsWithSlider} />;
}

function useNewComment() {
    const personaKey = usePersonaKey();
    const datastore = useDatastore();
    return useMemo(() => ({ text: '', key: datastore.getNewKey(), from: personaKey || '', time: datastore.getTime() }), [personaKey]);
}

/**
 * Renders the comment slider visualization at the top
 * of its parent structure.
 *
 * Displays the existing vote if one exists, otherwise
 * allows the user to interact with the comment slider selector.
 *
 * @param comments - The comments to display.
 * @returns The comment slider page top widget.
 */
function CommentSliderPageTopWidget({ comments }) {
    const personaKey = usePersonaKey();
    const myAnswer = useCollection(commentTable, { filter: { from: personaKey, replyTo: null, deleted: null } })[0];
    const newComment = useNewComment();

    const [editableComment, setEditableComment] = useState<CommentWithSlider | null>(null);
    const finalComment = editableComment ?? myAnswer ?? newComment;

    const [changingVote, setChangingVote] = useState(false);

    const s = CommentSliderPageTopWidgetStyle;

    return (
        <View>
            {(myAnswer as CommentWithSlider)?.slider && !changingVote && (
                <View style={s.visualizationContainer}>
                    <CommentSliderVisualizationRedirect comments={comments} />
                    <Center>
                        <PadBox vert={8}>
                            <CTAButton label={t('Change your vote')} onPress={() => setChangingVote(true)} />
                        </PadBox>
                    </Center>
                </View>
            )}
            {!((myAnswer as CommentWithSlider)?.slider && !changingVote) && (
                <View style={s.sliderContainer}>
                    <Selector comment={finalComment} setComment={setEditableComment} />
                    {(finalComment as any).slider && <SkipAndSubmitButton comment={finalComment} onPress={() => setChangingVote(false)} />}
                </View>
            )}
        </View>
    );
}

const CommentSliderPageTopWidgetStyle = StyleSheet.create({
    visualizationContainer: {
        gap: 24,
        padding: 20,
        backgroundColor: colorSurfaceSecondary,
        marginTop: 32,
        borderRadius: 12,
    },
    sliderContainer: {
        gap: 24,
        paddingTop: 32,
        paddingHorizontal: 20,
        display: 'flex',
    },
});

export type HorizBarVisualizationProps = { commentsWithSlider: readonly CommentWithSlider[] };

export function HorizBarVisualization({ commentsWithSlider }: HorizBarVisualizationProps) {
    const { commentSliderLabels } = useConfig<CommentSliderFeatureConfig>();
    const personaKey = usePersonaKey();
    const votes = commentsWithSlider.map((c) => commentSliderLabels[c.slider! - 1]);
    const myAnswer = commentsWithSlider.find((comment) => comment.from == personaKey && comment.replyTo == null);
    const myVote = myAnswer?.slider ? commentSliderLabels[myAnswer.slider - 1] : null;
    return (
        <PadBox top={20}>
            <PollResult myVote={myVote ? labelToKey(myVote) : null} optionLabels={commentSliderLabels} votes={votes} />
        </PadBox>
    );
}

function getCommentsWithSelectedPosition({ datastore, comment }) {
    const selection = datastore.getSessionData(['commentslider', 'selection']);
    if (!comment.text) {
        return false;
    } else if (selection != null) {
        return comment.slider == selection;
    } else {
        return true;
    }
}

export type CommentSliderEditCommentTopWidgetProps = { comment: CommentWithSlider; setComment: (comment: CommentWithSlider) => void };

/**
 * Renders the comment slider selector when editing a comment.
 *
 * If a slider value is passed in via screen params, it will
 * be used to set the slider value.
 *
 * @param comment - The comment to edit.
 * @param setComment - The function to set the comment.
 * @returns The edit comment top widget with selector.
 */
export function CommentSliderEditCommentTopWidget({ comment, setComment }: CommentSliderEditCommentTopWidgetProps) {
    const { sliderValue } = useScreenParams();

    // Read the slider value from screen params if it's present.
    useEffect(() => {
        if (sliderValue) {
            setComment({ ...comment, slider: Number(sliderValue) });
        }
    }, [sliderValue, setComment]);

    if (comment.replyTo) {
        return null;
    }

    return (
        <>
            <Selector comment={comment} setComment={setComment} />
            <Pad size={24} />
            <UtilityText label={t('Explain your response')} weight='medium' />
        </>
    );
}

export type SelectorProps = { comment: CommentWithSlider; setComment: (comment: CommentWithSlider) => void };

/**
 * Renders the comment slider selector.
 *
 * @param comment - The comment to edit.
 * @param setComment - The function to set the comment.
 * @returns The comment slider selector.
 */
export function Selector({ comment, setComment }: SelectorProps) {
    const { commentSliderLabels, commentSliderSelector, commentSliderDefault, commentSliderPrompt } =
        useConfig<CommentSliderFeatureConfig>();
    const datastore = useDatastore();

    function onSelectPosition(position) {
        setComment({ ...comment, slider: position });
        if (!comment.slider) {
            logEventAsync(datastore, 'comment-slider-pick', { text: String(position) });
        }
    }
    return (
        <>
            {React.createElement(commentSliderSelector, {
                value: comment.slider,
                optionLabels: commentSliderLabels,
                optionColors: ratingColors,
                defaultValue: commentSliderDefault,
                placeholder: commentSliderPrompt,
                onChangeValue: onSelectPosition,
            })}
        </>
    );
}

function SkipAndSubmitButton({ comment, onPress }) {
    const datastore = useDatastore();
    const existingComment = useObject(commentTable, comment.key);

    const isExistingCommentWithText = existingComment?.text?.length && existingComment?.text?.length > 0;
    const buttonPlaceholder = isExistingCommentWithText ? t('Update your response') : t('Share your thoughts');
    const buttonLabel = (existingComment as CommentWithSlider)?.slider ? t('Skip and change vote') : t('Skip and submit vote');

    return (
        <>
            <TextFieldButton
                onPress={() => {
                    datastore.needsLogin(() => {}, '', {
                        structureKey: datastore.getStructureKey(),
                        instanceKey: datastore.getInstanceKey(),
                        screenKey: 'composer',
                        params: {
                            sliderValue: comment.slider,
                        },
                    })();
                    onPress();
                }}
                placeholder={buttonPlaceholder}
            />
            <SubtleButton
                color={colorButtonAccent}
                icon={LoopIcon}
                label={buttonLabel}
                onPress={() => {
                    datastore.needsLogin(() => {}, '', {
                        structureKey: datastore.getStructureKey(),
                        instanceKey: datastore.getInstanceKey(),
                        screenKey: 'recordingVote',
                        params: {
                            sliderValue: comment.slider,
                            csrfToken: datastore.props.appData?.getCSRFToken(),
                        },
                    })();
                    onPress();
                }}
            />
        </>
    );
}

function AnswerInputOnlyForCommentWithText() {
    const personaKey = usePersonaKey();
    const myAnswer = useCollection(commentTable, { filter: { from: personaKey, replyTo: null, deleted: null } })[0];

    if (myAnswer && myAnswer?.text !== '') {
        return <MyAnswerPreview comment={myAnswer} />;
    }
    return null;
}

function State({ comment }) {
    const s = StateStyle;
    const { commentSliderLabels } = useConfig<CommentSliderFeatureConfig>();
    if (comment.slider) {
        return (
            <PadBox bottom={20}>
                <ColoredBallPill color={ratingColors[comment.slider - 1]} label={commentSliderLabels[comment.slider - 1]} />
            </PadBox>
        );
    } else {
        null;
    }
}
const StateStyle = StyleSheet.create({
    tag: {
        marginBottom: 20,
        backgroundColor: colorSurfaceSecondary,
        borderRadius: 100,
        paddingHorizontal: 12,
        paddingVertical: 6,
        alignSelf: 'flex-start',
    },
});

function countRatings(posts) {
    const ratingCounts = [0, 0, 0, 0, 0];
    for (const post of posts) {
        if (post.slider) {
            ratingCounts[post.slider - 1] += 1;
        }
    }
    return ratingCounts;
}

/**
 * Temporarily-displayed screen that handles the
 * recording of a vote when the user presses
 * "Skip and submit vote".
 *
 * @returns The recording vote screen.
 */
export function RecordingVoteScreen() {
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const { sliderValue, csrfToken } = useScreenParams();
    const newComment = useNewComment();
    const appData = useAppData();
    const myAnswer = useCollection(commentTable, { filter: { from: personaKey, replyTo: null, deleted: null } })[0];

    useEffect(() => {
        let timerId: NodeJS.Timeout;
        if (sliderValue && personaKey && appData?.isValidCSRFToken(csrfToken)) {
            // Artificial delay so the user doesn't think something is broken.
            timerId = setTimeout(() => {
                // Update the existing comment if one already exists.
                const comment = myAnswer
                    ? {
                          ...myAnswer,
                          slider: parseInt(sliderValue),
                      }
                    : {
                          ...newComment,
                          slider: parseInt(sliderValue),
                      };
                postCommentAsync({ datastore: datastore, comment: comment })
                    .then(() => {
                        datastore.goBack();
                    })
                    .catch((e) => {
                        // TODO
                    });
            }, 1000);
        } else {
            datastore.goBack();
        }
        return () => clearTimeout(timerId);
    }, [sliderValue, personaKey, csrfToken]);

    const s = RecordingVoteScreenStyle;
    return (
        <View style={s.container}>
            <PadBox horiz={20} vert={20}>
                <ActivityIndicator color={colorButtonAccent} size='large' />
                <Pad size={16} />
                <UtilityText label={t('Recording vote...')} weight='strong' />
            </PadBox>
        </View>
    );
}

const RecordingVoteScreenStyle = StyleSheet.create({
    container: {
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
